import { inject, injectable, injectFromBase } from 'inversify'
import Note from '@/data/models/domain/Note'
import type NoteRepository from '@/data/repositories/domain/noteRepository'
import { ModelEventType } from '@/services/transport/types'
import { DomainSettings } from '@/decorators/domainDecorators'
import type { NotePostBody } from '@/services/domain/note/types'
import DomainBaseService from '@/services/domain/domainBaseService'
import { SERVICE_TYPES } from '@/core/container/types'
import type EntityManagerService from '@/data/repositories/entityManagerService'
import type ModelSubscriptionService from '@/services/transport/modelSubscriptionService'
import type PreloaderManager from '@/services/preloaders/preloaderManager'
import type UserService from '@/services/domain/user/userService'
import type TeamWorkflowService from '@/services/domain/accounts/teamWorkflowService'
import type { ModelRaw } from '@/types'

@DomainSettings({
  model: Note,
})
@injectable()
@injectFromBase()
export default class NoteService extends DomainBaseService<NoteRepository> {
  constructor(
    @inject(SERVICE_TYPES.EntityManager) protected readonly entityManager: EntityManagerService,
    @inject(SERVICE_TYPES.ModelSubscriptionService) protected readonly subscription: ModelSubscriptionService,
    @inject(SERVICE_TYPES.PreloaderManager) protected readonly preloaderManager: PreloaderManager,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
    @inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService,
  ) {
    super(entityManager, subscription, preloaderManager)
  }

  public override isSelfUpdating(): boolean {
    return true
  }
  public async createNote(note: NotePostBody, contactId: string) {
    const result = await this.getDomainRepository().createNote(note, contactId)
    await this.insertOrUpdateItem(result.data)
    this.notify([result.data.id.toString()], ModelEventType.CREATE)
    return result
  }

  public async updateNote(note: NotePostBody, noteId: string) {
    note.id = noteId
    const result = await this.getDomainRepository().putForFormRequest<ModelRaw<Note>>(note)
    await this.insertOrUpdateItem(result.data)
    this.notify([result.data.id.toString()], ModelEventType.UPDATE)
    return result
  }

  public getNotesByIds(ids: string[], limit?: number): Note[] {
    return this.getDomainRepository().getNotesByIds(ids, limit)
  }

  public getTotalCount(ids: string[]): number {
    return this.getDomainRepository().getTotalCount(ids)
  }

  public isAllowEditNote(authorId: string) {
    return this.userService.currentUserId() === authorId
  }

  public isAllowDeleteNote(authorId: string) {
    return this.teamWorkflowService.isSubaccountsWorkflow() || this.isAllowEditNote(authorId)
  }

  public addNote(note: Pick<Note, 'id' | 'note' | 'userId' | 'createdAt'> & Partial<Pick<Note, 'attachmentFileIds'>>) {
    return this.getDomainRepository().queueInsertOrUpdate([note])
  }
}
